import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, History } from "lucide-react";
import { type ReactNode, useState } from "react";
import {
  Badge,
  Button,
  Card,
  DetailLayout,
  Dialog,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from "@rumbledirect/ui-v2";
import { isExternalRequest } from "@/configurator/requests/configuratorRequest";
import {
  requestEmployeeLabel,
  requestStatusLabel,
  requestStatusTone,
} from "@/configurator/requests/configuratorRequestFields";
import { AskQuotesDialog } from "@/configurator/requests/AskQuotesDialog";
import { RequestActiesCard } from "@/configurator/requests/RequestActiesCard";
import { RequestOrderDialog } from "@/configurator/requests/RequestOrderDialog";
import { RequestOpenTasksCard } from "@/configurator/requests/RequestOpenTasksCard";
import { RequestStepper } from "@/configurator/requests/RequestStepper";
import { AlgemeenTab } from "@/configurator/requests/tabs/AlgemeenTab";
import { FinalReportsTab } from "@/configurator/requests/tabs/FinalReportsTab";
import { ItemSectionsTab } from "@/configurator/requests/tabs/ItemSectionsTab";
import { PlaceholderCard } from "@/configurator/requests/tabs/PlaceholderCard";
import { QuotesTab } from "@/configurator/requests/tabs/QuotesTab";
import { useConfiguratorRequestDetail } from "@/configurator/requests/useConfiguratorRequestDetail";
import { useDeleteConfiguratorRequest } from "@/configurator/requests/useDeleteConfiguratorRequest";
import { useUpdateConfiguratorRequest } from "@/configurator/requests/useUpdateConfiguratorRequest";
import { useDealerOptions } from "@/configurator/vehicleTypes/useDealerOptions";
import { useCurrentUser } from "@/currentUser/CurrentUserProvider";
import { hasPermission } from "@/currentUser/permissions";
import { RequirePermission } from "@/currentUser/RequirePermission";
import { NOTE_READ_PERMISSION } from "@/notes/notePermissions";
import { NotesTab } from "@/notes/NotesTab";

const DETAIL_TABS = [
  { value: "algemeen", label: "Algemeen" },
  { value: "offertes", label: "Offertes" },
  { value: "eindrapporten", label: "Eindrapporten" },
  { value: "voertuig", label: "Voertuig" },
  { value: "inrichting", label: "Inrichting" },
  { value: "telematica", label: "Telematica" },
  { value: "werkzaamheden", label: "Aanvullende werkzaamheden" },
  { value: "notities", label: "Notities" },
  { value: "logboek", label: "Logboek" },
] as const;

type DetailTab = (typeof DETAIL_TABS)[number]["value"];

/**
 * Tabs that show what was configured. A request that was put together outside
 * the configurator has no lines at all, so these would each be an empty page
 * saying nothing; the description and the uploaded report say it instead.
 */
const CONFIGURATION_TABS: readonly DetailTab[] = [
  "voertuig",
  "inrichting",
  "telematica",
  "werkzaamheden",
];

/**
 * Tabs that are only offered to a user who may read what is behind them. A tab
 * missing from this map is open to everyone who may open the request at all.
 */
const TAB_PERMISSIONS: Partial<Record<DetailTab, string>> = {
  notities: NOTE_READ_PERMISSION,
};

const DEFAULT_TAB: DetailTab = "algemeen";

/** The open-tasks card lists work-queue tasks, which this permission reads. */
const TASK_READ_PERMISSION = "admin_work_queue_task_read";

function isDetailTab(value: unknown): value is DetailTab {
  return DETAIL_TABS.some((tab) => tab.value === value);
}

export const Route = createFileRoute(
  "/_authenticated/configurator/requests_/$configuratorRequestId",
)({
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab: DetailTab; task?: string } => ({
    tab: isDetailTab(search.tab) ? search.tab : DEFAULT_TAB,
    // The task whose sheet is open, so a shared link or reload reopens it.
    task:
      typeof search.task === "string" && search.task !== ""
        ? search.task
        : undefined,
  }),
  component: ConfiguratorRequestDetailPage,
});

function ConfiguratorRequestDetailPage(): ReactNode {
  const { configuratorRequestId } = Route.useParams();
  const { tab, task: openTaskId } = Route.useSearch();
  const navigateSearch = Route.useNavigate();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();

  const { request, isPending, isError, refetch, ...related } =
    useConfiguratorRequestDetail(configuratorRequestId);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [asking, setAsking] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const trimmedDeclineReason = declineReason.trim();

  const updateMutation = useUpdateConfiguratorRequest(configuratorRequestId);
  const deleteMutation = useDeleteConfiguratorRequest();

  // Only loaded once the dialog is opened. The vehicle type decides which of
  // the dealerships the vehicle can be ordered at; without one it is any.
  const {
    options: dealerOptions,
    isPending: dealersPending,
    isError: dealersFailed,
    refetch: refetchDealers,
  } = useDealerOptions({ enabled: ordering });
  const orderableDealerships = request?.orderableDealerships;
  const orderableDealerOptions = orderableDealerships
    ? dealerOptions.filter((option) =>
        orderableDealerships.includes(option.value),
      )
    : dealerOptions;

  // `tab` comes from the URL, but a gated tab renders neither a trigger nor a
  // panel for a user without its permission, and the configuration tabs are
  // left out entirely for a request that was put together elsewhere. Leaving
  // the Tabs root on such a value blanks the whole tab strip rather than just
  // that one panel, so resolve the effective tab here.
  const isExternal = request !== undefined && isExternalRequest(request);
  const requiredPermission = TAB_PERMISSIONS[tab];
  const isReachable =
    !(requiredPermission && !hasPermission(currentUser, requiredPermission)) &&
    !(isExternal && CONFIGURATION_TABS.includes(tab));
  const activeTab = isReachable ? tab : DEFAULT_TAB;

  return (
    <div className="space-y-5">
      <Link
        to="/configurator/requests"
        search={{ page: 1 }}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Terug naar aanvragen
      </Link>

      {isError ? (
        <Card
          role="alert"
          className="flex flex-col items-center gap-3 py-10 text-center"
        >
          <p className="text-muted-foreground">
            We konden deze aanvraag niet laden.
          </p>
          <Button variant="outline" size="sm" onClick={refetch}>
            Opnieuw proberen
          </Button>
        </Card>
      ) : isPending || !request ? (
        <>
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-64 w-full" />
        </>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              {requestEmployeeLabel(request)}
            </h1>
            <Badge variant={requestStatusTone(request.status)} size="sm">
              {requestStatusLabel(request.status)}
            </Badge>
          </div>

          {/*
            The rail sits beside the tabs so a decision can be taken from any
            of them. On a phone it leads, minus the stepper: that is context
            rather than something to act on.
          */}
          <DetailLayout
            railFirst
            rail={
              <>
                <RequestActiesCard
                  request={request}
                  actions={{
                    isSubmitting: updateMutation.isPending,
                    onApprove: () =>
                      updateMutation.mutate({ status: "approved" }),
                    onDecline: () => {
                      setDeclineReason(request.declineReason ?? "");
                      setDeclining(true);
                    },
                    onAskQuotes: () => setAsking(true),
                    onOrder: () => setOrdering(true),
                    onDelete: () => setConfirmDelete(true),
                  }}
                />
                <RequestStepper request={request} className="hidden lg:block" />
                {hasPermission(currentUser, TASK_READ_PERMISSION) && (
                  <RequestOpenTasksCard
                    configuratorRequestId={configuratorRequestId}
                    openTaskId={openTaskId}
                    onOpenTask={(taskId) => {
                      void navigateSearch({
                        search: (previous) => ({ ...previous, task: taskId }),
                      });
                    }}
                    onCloseTask={() => {
                      void navigateSearch({
                        search: (previous) => ({
                          ...previous,
                          task: undefined,
                        }),
                      });
                    }}
                  />
                )}
              </>
            }
          >
            <Tabs
              value={activeTab}
              onValueChange={(next) =>
                void navigateSearch({
                  search: () => ({
                    tab: isDetailTab(next) ? next : DEFAULT_TAB,
                  }),
                })
              }
              className="flex flex-col gap-5"
            >
              <TabsList className="self-start">
                {DETAIL_TABS.map((detailTab) => {
                  if (
                    isExternal &&
                    CONFIGURATION_TABS.includes(detailTab.value)
                  )
                    return null;

                  const trigger = (
                    <TabsTrigger key={detailTab.value} value={detailTab.value}>
                      {detailTab.label}
                    </TabsTrigger>
                  );
                  const permission = TAB_PERMISSIONS[detailTab.value];

                  return permission === undefined ? (
                    trigger
                  ) : (
                    <RequirePermission
                      key={detailTab.value}
                      permission={permission}
                    >
                      {trigger}
                    </RequirePermission>
                  );
                })}
              </TabsList>

              <TabsContent value="algemeen">
                <AlgemeenTab
                  request={request}
                  conversion={related.conversion}
                  vehicleType={related.vehicleType}
                  vehicleCategory={related.vehicleCategory}
                />
              </TabsContent>

              <TabsContent value="eindrapporten">
                <FinalReportsTab
                  configuratorRequestId={configuratorRequestId}
                />
              </TabsContent>

              <TabsContent value="offertes">
                <QuotesTab
                  configuratorRequestId={configuratorRequestId}
                  requestStatus={request.status}
                />
              </TabsContent>

              {isExternal ? null : (
                <>
                  <TabsContent value="voertuig">
                    <ItemSectionsTab tab="voertuig" items={request.items} />
                  </TabsContent>

                  <TabsContent value="inrichting">
                    <ItemSectionsTab tab="inrichting" items={request.items} />
                  </TabsContent>

                  <TabsContent value="telematica">
                    <ItemSectionsTab tab="telematica" items={request.items} />
                  </TabsContent>

                  <TabsContent value="werkzaamheden">
                    <ItemSectionsTab
                      tab="werkzaamheden"
                      items={request.items}
                    />
                  </TabsContent>
                </>
              )}

              <RequirePermission permission={NOTE_READ_PERMISSION}>
                <TabsContent value="notities">
                  <NotesTab
                    subject={{
                      type: "configuratorRequest",
                      id: configuratorRequestId,
                    }}
                  />
                </TabsContent>
              </RequirePermission>

              <TabsContent value="logboek">
                <PlaceholderCard
                  icon={History}
                  title="Logboek"
                  description="Het logboek van een aanvraag wordt nog niet bijgehouden. Dit volgt zodra de backend de gebeurtenissen vastlegt."
                />
              </TabsContent>
            </Tabs>
          </DetailLayout>

          <AskQuotesDialog
            configuratorRequestId={configuratorRequestId}
            open={asking}
            onOpenChange={setAsking}
          />

          <RequestOrderDialog
            // Remount when the filed number changes, so the input follows what
            // was saved instead of holding on to the value it opened with.
            key={request.leaseContractNumber ?? ""}
            open={ordering}
            leaseContractNumber={request.leaseContractNumber ?? ""}
            dealerOptions={orderableDealerOptions}
            dealersRestricted={orderableDealerships != null}
            isLoadingDealers={dealersPending}
            dealersFailed={dealersFailed}
            onRetryDealers={refetchDealers}
            isSubmitting={updateMutation.isPending}
            onOpenChange={setOrdering}
            onSubmit={(details, onSuccess) =>
              updateMutation.mutate(details, { onSuccess })
            }
          />

          <Dialog
            open={declining}
            onOpenChange={setDeclining}
            title="Aanvraag afwijzen"
            description={
              request.status === "submitted"
                ? "Laat de medewerker weten waarom de aanvraag niet doorgaat."
                : "Deze aanvraag is al akkoord. Afwijzen trekt dat in en legt hem terug bij de medewerker, die hem daarna kan corrigeren."
            }
            footer={
              <>
                <Button
                  variant="ghost"
                  onClick={() => setDeclining(false)}
                  disabled={updateMutation.isPending}
                >
                  Annuleren
                </Button>
                <Button
                  variant="destructive"
                  disabled={
                    updateMutation.isPending || trimmedDeclineReason === ""
                  }
                  onClick={() =>
                    updateMutation.mutate(
                      {
                        status: "declined",
                        declineReason: trimmedDeclineReason,
                      },
                      { onSuccess: () => setDeclining(false) },
                    )
                  }
                >
                  Afwijzen
                </Button>
              </>
            }
          >
            <Textarea
              aria-label="Reden van afwijzing"
              placeholder="Bijvoorbeeld: valt buiten de afgesproken kostennorm."
              value={declineReason}
              maxLength={255}
              onChange={(event) => setDeclineReason(event.target.value)}
            />
            <p className="mt-2 text-xs text-muted-foreground">
              Een reden is verplicht: de medewerker ziet deze tekst terug.
            </p>
          </Dialog>

          <Dialog
            open={confirmDelete}
            onOpenChange={setConfirmDelete}
            title="Aanvraag verwijderen"
            description={`Weet je zeker dat je de aanvraag van ${requestEmployeeLabel(request)} wilt verwijderen? De uitnodiging waarmee de aanvraag is ingediend verdwijnt mee; opnieuw laten configureren vraagt om een nieuwe uitnodiging.`}
            footer={
              <>
                <Button
                  variant="ghost"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleteMutation.isPending}
                >
                  Annuleren
                </Button>
                <Button
                  variant="destructive"
                  disabled={deleteMutation.isPending}
                  onClick={() =>
                    deleteMutation.mutate(configuratorRequestId, {
                      onSuccess: () => {
                        setConfirmDelete(false);
                        void navigate({
                          to: "/configurator/requests",
                          search: { page: 1 },
                        });
                      },
                    })
                  }
                >
                  Verwijderen
                </Button>
              </>
            }
          />
        </>
      )}
    </div>
  );
}
